import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Download,
  Loader2,
  Mail,
  MailOpen,
  Trash2,
} from "lucide-react";
import { Card, CardContent } from "@cms/ui/components/card";
import { Badge } from "@cms/ui/components/badge";
import { Button } from "@cms/ui/components/button";
import { Checkbox } from "@cms/ui/components/checkbox";
import { Input } from "@cms/ui/components/input";
import { Label } from "@cms/ui/components/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@cms/ui/components/select";
import { ConfirmDeleteDialog } from "@cms/ui/components/confirm-dialog";
import { ErrorAlert } from "@cms/ui/components/error-alert";
import { LoadingSpinner } from "@cms/ui/components/loading-spinner";
import { formatTimestamp } from "@cms/portal-lib/format";
import { useApiQuery } from "@cms/portal-lib/hooks";
import { PaginationControls } from "@cms/ui/components/pagination-controls";
import {
  deleteSubmission,
  deleteSubmissions,
  downloadSubmissionsExport,
  fetchSubmissions,
  setSubmissionRead,
  submissionFileValue,
  type Form,
  type Submission,
  type SubmissionFilters,
} from "../lib/forms";
import type { ApiClient } from "../lib/api";

const DEFAULT_PER_PAGE = 20;

// Radix Select items can't carry an empty value, so "every form" gets a sentinel.
const ALL_FORMS = "__all__";

/** First few string values of a submission, joined for the collapsed row. */
function previewText(data: Record<string, unknown>): string {
  const values = Object.values(data).filter((value): value is string => typeof value === "string");
  const first = values[0] ?? "";
  const rest = values.slice(1).join(" — ");
  const snippet = rest.length > 80 ? `${rest.slice(0, 80)}…` : rest;
  return [first, snippet].filter(Boolean).join(" — ");
}

/** Filter fields + page, with any filter change snapping back to page 1. */
function useSubmissionFilters() {
  const [formId, setFormId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPageValue] = useState(DEFAULT_PER_PAGE);

  const filters: SubmissionFilters = useMemo(
    () => ({
      formId: formId || undefined,
      from: from || undefined,
      to: to || undefined,
      q: q || undefined,
    }),
    [formId, from, to, q]
  );

  const applyFilter = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  return {
    filters,
    page,
    setPage,
    perPage,
    setPerPage: (next: number) => {
      setPerPageValue(next);
      setPage(1);
    },
    formId,
    setFormId: applyFilter(setFormId),
    from,
    setFrom: applyFilter(setFrom),
    to,
    setTo: applyFilter(setTo),
    searchInput,
    setSearchInput,
    applySearch: () => applyFilter(setQ)(searchInput.trim()),
    hasFilters: Boolean(formId || from || to || q),
  };
}

interface SubmissionsInboxProps {
  api: ApiClient | null;
  /** All forms, for the filter dropdown and for labelling submission fields. */
  forms: Form[];
  /** Public R2 base URL for linking uploaded files; null when unresolved. */
  assetsBaseUrl: string | null;
  /** Keeps the sidebar's unread badge honest after read/delete actions. */
  refreshStatus: () => void | Promise<void>;
  /** Bump to force a reload (e.g. after a form and its submissions were deleted). */
  reloadKey: number;
}

/** The submissions inbox: filters, expandable rows, bulk actions and exports. */
export function SubmissionsInbox({
  api,
  forms,
  assetsBaseUrl,
  refreshStatus,
  reloadKey,
}: SubmissionsInboxProps) {
  const inboxFilters = useSubmissionFilters();
  const { filters, page, setPage, perPage, setPerPage } = inboxFilters;

  const query = useApiQuery(
    () =>
      api
        ? fetchSubmissions(api, { page, perPage, ...filters })
        : null,
    [api, page, perPage, filters, reloadKey]
  );
  const result = query.data;

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState("");
  const [actionError, setActionError] = useState("");

  // Fresh results invalidate the selection (rows may have moved or gone).
  useEffect(() => {
    setSelected(new Set());
  }, [result]);

  const formsById = useMemo(() => new Map(forms.map((form) => [form.id, form])), [forms]);
  const formName = (formId: string) => formsById.get(formId)?.name ?? formId;

  /** Order + label a submission's entries using its form definition. */
  const displayEntries = (submission: Submission) => {
    const form = formsById.get(submission.form_id);
    const entries = Object.entries(submission.data);
    if (!form) return entries.map(([key, value]) => ({ key, label: key, value }));
    const byId = new Map(entries);
    const ordered = [];
    for (const field of form.fields) {
      if (field.id && byId.has(field.id)) {
        ordered.push({ key: field.id, label: field.label, value: byId.get(field.id) });
        byId.delete(field.id);
      }
    }
    for (const [key, value] of byId) ordered.push({ key, label: key, value });
    return ordered;
  };

  const renderValue = (value: unknown): React.ReactNode => {
    const file = submissionFileValue(value);
    if (file) {
      const url = assetsBaseUrl ? `${assetsBaseUrl}/${file.key}` : null;
      const size = ` (${Math.max(Math.ceil((file.size ?? 0) / 1024), 1)}KB)`;
      return url ? (
        <a href={url} target="_blank" rel="noreferrer" className="underline">
          {file.name || "Download file"}{size}
        </a>
      ) : (
        `${file.name || "file"}${size}`
      );
    }
    if (Array.isArray(value)) return value.join(", ");
    return String(value);
  };

  const setRead = async (submission: Submission, read: boolean) => {
    if (!api) return;
    try {
      await setSubmissionRead(api, submission.id, read);
      query.setData((prev) => prev && {
        ...prev,
        unread: prev.unread + (read ? -1 : 1),
        submissions: prev.submissions.map((s) => (s.id === submission.id ? { ...s, read } : s)),
      });
      refreshStatus();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    }
  };

  const toggleExpand = (submission: Submission) => {
    const opening = expandedId !== submission.id;
    setExpandedId(opening ? submission.id : null);
    if (opening && !submission.read) {
      setRead(submission, true);
    }
  };

  const toggleSelected = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allVisibleSelected =
    !!result && result.submissions.length > 0 && result.submissions.every((s) => selected.has(s.id));

  const toggleSelectAll = () => {
    setSelected(() =>
      allVisibleSelected || !result ? new Set() : new Set(result.submissions.map((s) => s.id))
    );
  };

  const removeOne = async (submission: Submission) => {
    if (!api) return;
    setDeleting(true);
    try {
      await deleteSubmission(api, submission.id);
      setExpandedId(null);
      refreshStatus();
      query.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleting(false);
    }
  };

  const removeSelected = async () => {
    if (!api) return;
    setDeleting(true);
    try {
      await deleteSubmissions(api, [...selected]);
      setConfirmBulkDelete(false);
      refreshStatus();
      query.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleting(false);
    }
  };

  const exportAs = async (format: "csv" | "json") => {
    if (!api) return;
    setExporting(format);
    setActionError("");
    try {
      await downloadSubmissionsExport(api, format, filters);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setExporting("");
    }
  };

  const total = result?.total ?? 0;

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="text-base font-semibold">Submissions</h2>
        {!!result && result.unread > 0 && <Badge variant="default">{result.unread} unread</Badge>}
        <div className="ml-auto flex items-center gap-2">
          {selected.size > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive"
              onClick={() => setConfirmBulkDelete(true)}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete selected ({selected.size})
            </Button>
          )}
          <Button variant="outline" size="sm" disabled={!!exporting} onClick={() => exportAs("csv")}>
            {exporting === "csv" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            CSV
          </Button>
          <Button variant="outline" size="sm" disabled={!!exporting} onClick={() => exportAs("json")}>
            {exporting === "json" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            JSON
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label htmlFor="filter-form" className="text-xs">Form</Label>
          <Select
            value={inboxFilters.formId || ALL_FORMS}
            onValueChange={(value: string) => inboxFilters.setFormId(value === ALL_FORMS ? "" : value)}
          >
            <SelectTrigger id="filter-form" className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_FORMS}>All forms</SelectItem>
              <SelectItem value="contact">Contact (built-in)</SelectItem>
              {forms.map((form) => (
                <SelectItem key={form.id} value={form.id}>{form.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="filter-from" className="text-xs">From</Label>
          <Input
            id="filter-from"
            type="date"
            className="w-40"
            value={inboxFilters.from}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => inboxFilters.setFrom(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="filter-to" className="text-xs">To</Label>
          <Input
            id="filter-to"
            type="date"
            className="w-40"
            value={inboxFilters.to}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => inboxFilters.setTo(e.target.value)}
          />
        </div>
        <div className="min-w-48 flex-1 space-y-1">
          <Label htmlFor="filter-q" className="text-xs">Search (press Enter)</Label>
          <Input
            id="filter-q"
            type="search"
            placeholder="Search submissions…"
            value={inboxFilters.searchInput}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => inboxFilters.setSearchInput(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent) => e.key === "Enter" && inboxFilters.applySearch()}
            onBlur={() => inboxFilters.applySearch()}
          />
        </div>
      </div>

      <ErrorAlert error={query.error || actionError} />

      {!result && !query.error && <LoadingSpinner className="py-20" />}

      {result && (
        <Card>
          <CardContent className="p-0">
            {result.submissions.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-muted-foreground">
                No submissions{inboxFilters.hasFilters ? " match these filters" : " yet — messages from your website's forms will appear here"}.
              </p>
            ) : (
              <>
                <div className="flex items-center gap-3 border-b px-4 py-2">
                  <Checkbox
                    aria-label="Select all on this page"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                  />
                  <span className="text-xs text-muted-foreground">Select all on this page</span>
                </div>
                <ul className="divide-y">
                  {result.submissions.map((submission) => (
                    <li key={submission.id}>
                      <div className="flex w-full items-center gap-3 px-4 py-3 hover:bg-muted/50">
                        <Checkbox
                          aria-label={`Select submission ${submission.id}`}
                          checked={selected.has(submission.id)}
                          onChange={() => toggleSelected(submission.id)}
                        />
                        <button
                          type="button"
                          onClick={() => toggleExpand(submission)}
                          className="flex min-w-0 flex-1 items-center gap-3 text-left"
                        >
                          <span className={`h-2 w-2 shrink-0 rounded-full ${submission.read ? "bg-transparent" : "bg-primary"}`} />
                          <div className="min-w-0 flex-1">
                            <p className={`truncate text-sm ${submission.read ? "text-muted-foreground" : "font-semibold"}`}>
                              {previewText(submission.data) || "(no preview)"}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formName(submission.form_id)} · {formatTimestamp(submission.created_at)}
                            </p>
                          </div>
                          {expandedId === submission.id
                            ? <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
                            : <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />}
                        </button>
                      </div>

                      {expandedId === submission.id && (
                        <div className="space-y-2 border-t bg-muted/30 px-4 py-3">
                          <dl className="space-y-1.5">
                            {displayEntries(submission).map(({ key, label, value }) => (
                              <div key={key} className="grid grid-cols-[160px_1fr] gap-2 text-sm">
                                <dt className="font-medium text-muted-foreground">{label}</dt>
                                <dd className="whitespace-pre-wrap break-words">{renderValue(value)}</dd>
                              </div>
                            ))}
                          </dl>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setRead(submission, !submission.read)}
                            >
                              {submission.read ? <Mail className="h-3.5 w-3.5" /> : <MailOpen className="h-3.5 w-3.5" />}
                              Mark as {submission.read ? "unread" : "read"}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-muted-foreground hover:text-destructive"
                              disabled={deleting}
                              onClick={() => removeOne(submission)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {result && (
        <PaginationControls
          page={page}
          total={total}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
          itemsLabel="submissions"
          className="mt-4"
        />
      )}

      <ConfirmDeleteDialog
        open={confirmBulkDelete}
        onOpenChange={setConfirmBulkDelete}
        title={`Delete ${selected.size} submission${selected.size === 1 ? "" : "s"}?`}
        description="This permanently deletes the selected submissions and any files uploaded with them. This can't be undone."
        confirmLabel="Delete"
        busy={deleting}
        onConfirm={removeSelected}
      />
    </>
  );
}
